/**
 * Quando passare a un manifest nuovo (API.md §3): si continua col vecchio finché gli asset delle
 * voci attive del nuovo non sono in cache. Poi il controller applica il nuovo a fine contenuto
 * (o al cambio di segmento in sync).
 * Per non restare bloccati sul vecchio per sempre (asset che non si scarica mai) c'è un tetto di attesa.
 */
import { activeScheduleIds } from '../engine/schedule-engine';
import type { Manifest } from '../types';

export const MAX_ADOPT_WAIT_MS = 10 * 60 * 1000;

/** Id degli asset delle voci attive in `t` nel manifest (più il fallback, che serve quando non c'è altro). */
export function neededAssetIds(manifest: Manifest, t: number): string[] {
  const active = new Set(activeScheduleIds(manifest, t));
  const ids = new Set<string>();
  for (const s of manifest.schedule) if (active.has(s.id)) ids.add(s.asset_id);
  if (ids.size === 0 && manifest.fallback_asset_id) ids.add(manifest.fallback_asset_id);
  return [...ids].filter((id) => manifest.assets.some((a) => a.id === id));
}

export function canAdopt(
  pending: Manifest,
  isReady: (assetId: string, sha256: string) => boolean,
  t: number,
  waitedMs: number,
  hasCurrent: boolean,
): boolean {
  if (!hasCurrent) return true; // niente da preservare: parti subito
  if (waitedMs >= MAX_ADOPT_WAIT_MS) return true;
  const byId = new Map(pending.assets.map((a) => [a.id, a]));
  return neededAssetIds(pending, t).every((id) => {
    const a = byId.get(id);
    return a !== undefined && isReady(a.id, a.sha256);
  });
}
